import React, { useState } from 'react';
import { TextPairingProps } from '@components/TextPairing';
import { Columns } from '@components/Columns';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import ReactHtmlParser from 'react-html-parser';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

import {
  StyledPlaceholderCloudImage,
  MainTextPairing,
  BookingAssistantTextPairing,
  TravelRecommendationsTextPairing,
  MapAndAppTextPairing,
  StyledContentBox,
  BackgroundImageContainer,
  MapAndAppImagesBlock,
  ImageColumn,
  TextColumn,
  ReversedBlockColumns,
  DesktopLazyLoadWrapper,
  MobileLazyLoadWrapper,
  DesktopPlaceholderCloudImage,
  MobilePlaceholderCloudImage,
  StyledTravelBackgroundMapElements,
  BackgroundStyledPlaceholderCloudImage,
  RecommendStyledPlaceholderCloudImage,
} from './TravelSection.styles';
import { useTranslation } from 'react-i18next';

const subBlockTextPairingProps: TextPairingProps = {
  eyebrowVariant: 'sectionEyeBrow24',
  eyebrowColor: 'humana4',
  titleTagName: 'h2',
  titleVariant: 'hed56',
  titleColor: 'neutra6',
  bodyVariant: 'dek20',
  bodyColor: 'neutra5',
  alignment: 'left',
  title: '',
};

export const TravelSection = () => {
  const sectionId = 'Guest_Travel_Tools';
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);
  const [isScrolledIntoMobile, setScrolledIntoMobile] = useState(false);
  const isMobileDevice = useThemeMediaQuery('below_sm');
  const { t } = useTranslation('weddingWebsitePage');
  const columnData = t('sections.travel.columns');

  return (
    <StyledContentBox as={'section'} paddingTop={[11, 14]} paddingBottom={[11, 15]} id={sectionId}>
      <DesktopLazyLoadWrapper>
        <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)}>
          <div />
        </LazyLoadWrapper>
      </DesktopLazyLoadWrapper>
      <MobileLazyLoadWrapper>
        <LazyLoadWrapper beforeLoad={() => setScrolledIntoMobile(true)}>
          <div />
        </LazyLoadWrapper>
      </MobileLazyLoadWrapper>
      <BackgroundImageContainer>
        <BackgroundStyledPlaceholderCloudImage
          src={'wedding-website/guest-travel-tools/travel-background_3.svg'}
          alt="Map"
          isMainSrcShown={isScrolledIntoView || isScrolledIntoMobile}
          optimization="default"
        />
        {isScrolledIntoView ? <StyledTravelBackgroundMapElements className="animate-pointers" /> : null}
      </BackgroundImageContainer>
      <Columns>
        <Columns.Column width="full">
          <MainTextPairing
            eyebrow={columnData[0].eyebrow}
            eyebrowTagName="h2"
            eyebrowVariant={'calloutHed32'}
            eyebrowColor="humana4"
            title={columnData[0].title}
            titleTagName="h2"
            titleVariant={'hed120Super'}
            titleColor="neutra6"
            alignment="center"
          />
        </Columns.Column>
      </Columns>
      <ReversedBlockColumns marginTop={[7, 13]} verticalAlign="center">
        <ImageColumn width="half">
          <StyledPlaceholderCloudImage
            src="wedding-website/guest-travel-tools/travel-booking-assistant.svg"
            alt="Travel booking assistant"
            height="537px"
            width="536px"
            isMainSrcShown={isScrolledIntoView || isScrolledIntoMobile}
            optimization="default"
          />
        </ImageColumn>
        <TextColumn width="half">
          <BookingAssistantTextPairing
            {...subBlockTextPairingProps}
            eyebrow={columnData[1].eyebrow}
            eyebrowTagName="h3"
            title={columnData[1].title}
            titleTagName="h3"
            body={isMobileDevice ? columnData[1].contentMobile : columnData[1].content}
          />
        </TextColumn>
      </ReversedBlockColumns>
      <Columns marginTop={[7, 13]} verticalAlign="center">
        <ImageColumn width="half">
          <RecommendStyledPlaceholderCloudImage
            src="wedding-website/guest-travel-tools/travel-recommendations.svg"
            alt="Travel recommendations"
            isMainSrcShown={isScrolledIntoView || isScrolledIntoMobile}
            width="352px"
            height="498px"
            optimization="default"
          />
        </ImageColumn>
        <TextColumn width="half">
          <TravelRecommendationsTextPairing
            {...subBlockTextPairingProps}
            eyebrow={columnData[2].eyebrow}
            eyebrowTagName="h3"
            title={ReactHtmlParser(isMobileDevice ? columnData[2].titleMobile : columnData[2].title)}
            titleTagName="h3"
            body={columnData[2].content}
          />
        </TextColumn>
      </Columns>
      <ReversedBlockColumns marginTop={[7, 13]} verticalAlign="flex-end">
        <ImageColumn width="half">
          <MapAndAppImagesBlock>
            <DesktopPlaceholderCloudImage
              src="wedding-website/guest-travel-tools/travel-map-directions-v2.svg"
              alt="Map directions"
              isMainSrcShown={isScrolledIntoView}
              width="399px"
              height="117px"
              optimization="default"
            />
            <DesktopPlaceholderCloudImage
              src="wedding-website/guest-travel-tools/travel-map-ride-v2.svg"
              alt="Request ride"
              isMainSrcShown={isScrolledIntoView}
              width="387px"
              height="117px"
              optimization="default"
            />
            <MobilePlaceholderCloudImage
              src="wedding-website/guest-travel-tools/map-app-mobile.png"
              alt="Request ride"
              isMainSrcShown={isScrolledIntoMobile}
              width="387px"
              height="100%"
              optimization="default"
            />
          </MapAndAppImagesBlock>
        </ImageColumn>
        <TextColumn width="half">
          <MapAndAppTextPairing
            {...subBlockTextPairingProps}
            eyebrow={columnData[3].eyebrow}
            eyebrowTagName="h3"
            title={columnData[3].title}
            titleTagName="h3"
            body={columnData[3].content}
          />
        </TextColumn>
      </ReversedBlockColumns>
    </StyledContentBox>
  );
};
